import config from '@/config';
import axiosInstance from '@/school-management/utils/api';
import {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
  GradeAutocompleteItem,
  ClassCodeAutocompleteItem,
  AutocompleteRequest,
  AutocompleteResponse,
} from '@/school-management/types/response/autocomplete';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';

export interface AutocompleteService {
  getTeachers: (
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ) => Promise<AutocompleteResponse<TeacherAutocompleteItem>>;
  getStudents: (
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ) => Promise<AutocompleteResponse<StudentAutocompleteItem>>;
  getGroups: (
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ) => Promise<AutocompleteResponse<GroupAutocompleteItem>>;
  getGrades: (
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ) => Promise<AutocompleteResponse<GradeAutocompleteItem>>;
  getClassCodes: (
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ) => Promise<AutocompleteResponse<ClassCodeAutocompleteItem>>;
}

function buildRequestBody(
  accountId: string,
  search: string | undefined,
  filters: CascadingFilterRequest | undefined,
  extraFilters?: Partial<CascadingFilterRequest>
): AutocompleteRequest {
  const body: AutocompleteRequest = {
    accountId,
    pageSize: 20,
  };

  if (search && search.trim()) {
    body.search = search.trim();
  }

  const cascading: CascadingFilterRequest = {};
  if (filters?.groupIds?.length) cascading.groupIds = filters.groupIds;
  if (filters?.teacherIds?.length) cascading.teacherIds = filters.teacherIds;
  if (filters?.studentIds?.length) cascading.studentIds = filters.studentIds;
  if (filters?.gradeIds?.length) cascading.gradeIds = filters.gradeIds;
  if (filters?.classCodes?.length) cascading.classCodes = filters.classCodes;

  // Merge any extra filter flags (e.g. isActive)
  if (extraFilters) {
    Object.assign(cascading, extraFilters);
  }

  if (Object.keys(cascading).length > 0) {
    body.filters = cascading;
  }

  return body;
}

export const autocompleteService: AutocompleteService = {
  async getTeachers(
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ): Promise<AutocompleteResponse<TeacherAutocompleteItem>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/autocomplete/teachers`;
    const { data } = await axiosInstance.post<AutocompleteResponse<TeacherAutocompleteItem>>(
      url,
      buildRequestBody(organizationId, search, filters)
    );
    return data;
  },

  async getStudents(
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ): Promise<AutocompleteResponse<StudentAutocompleteItem>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/autocomplete/students`;
    const { data } = await axiosInstance.post<AutocompleteResponse<StudentAutocompleteItem>>(
      url,
      buildRequestBody(organizationId, search, filters)
    );
    return data;
  },

  async getGroups(
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ): Promise<AutocompleteResponse<GroupAutocompleteItem>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/autocomplete/groups`;
    const { data } = await axiosInstance.post<AutocompleteResponse<GroupAutocompleteItem>>(
      url,
      buildRequestBody(organizationId, search, filters)
    );
    return data;
  },

  async getGrades(
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ): Promise<AutocompleteResponse<GradeAutocompleteItem>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/autocomplete/grades`;
    const { data } = await axiosInstance.post<AutocompleteResponse<GradeAutocompleteItem>>(
      url,
      buildRequestBody(organizationId, search, filters)
    );
    return data;
  },

  async getClassCodes(
    organizationId: string,
    search?: string,
    filters?: CascadingFilterRequest
  ): Promise<AutocompleteResponse<ClassCodeAutocompleteItem>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/autocomplete/class-codes`;
    const { data } = await axiosInstance.post<AutocompleteResponse<ClassCodeAutocompleteItem>>(
      url,
      buildRequestBody(organizationId, search, filters)
    );
    return data;
  },
};
